import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import {
  AUCTION_CATEGORIES,
  AUCTION_SORT_ORDER_OPTIONS,
  SORT_ORDER_OPTIONS,
} from '@propertypal/shared/src/constants/filterOptions';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import getTotalProperties from '@propertypal/shared/src/utils/search/getTotalProperties';
import Button from '@propertypal/web-ui/src/buttons/Button';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import { closeTooltip, openTooltip } from '@propertypal/web-ui/src/notifications/Tooltip';
import DidYouMean from '@propertypal/web-ui/src/search/DidYouMean';
import { Bold, Heading, Text } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import OpenViewingBox from './OpenViewingBox';
import { Container, Title, SideOptions, InfoContent, SaveSearchWrapper } from './SearchTitle.style';
import UpcomingAuctionsBox from './UpcomingAuctionsBox';

interface Props {
  handleSortChange: (sort: string) => void;
  onAlternativeSearch: (term: string) => void;
  onSaveSearchClick: () => void;
  searchState: UseSearchState;
  onMap?: boolean;
}

const SearchTitle: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const { appFilters, nlpFilters, isSavable, disableSaveSearch } = props.searchState;
  const filters = { ...appFilters, ...nlpFilters };
  const properties = useSelector(selectProperties);
  const showTotalProperties = !['COUNTRY', 'REGION'].includes(properties.data?.locationDetail?.termType || 'COUNTRY');
  const isBeyondLastPage =
    properties.data && properties.data.totalPages > 0 && properties.data.page + 1 > properties.data.totalPages;
  const showNoResults = properties.data?.results && properties.data.results.length === 0 && !isBeyondLastPage;
  const [propertyType, formattedTotal] = getTotalProperties(filters.category, properties.data);

  if (props.onMap) {
    return null;
  }

  return (
    <div>
      <Container>
        <div>
          {properties.data && !showNoResults && (
            <>
              <Title id="search-title">
                {properties.data.description.stem}
                <Text as="span" mt={5} style={{ display: 'block' }}>
                  {properties.data.description.details}
                </Text>
              </Title>

              <Text mt={5}>
                {properties.data && (
                  <>
                    Page {properties.data.page + 1} of {properties.data.totalPages}
                    {showTotalProperties && formattedTotal !== '0' && (
                      <>
                        {' '}
                        (<Bold color={theme.primary}>{formattedTotal}</Bold> {`${propertyType})`}
                      </>
                    )}
                  </>
                )}
              </Text>
            </>
          )}

          {showNoResults && (
            <>
              <Heading data-testid="noPropertiesHeading">No Properties Found</Heading>

              <Text mt={5}>
                Sorry. We couldn&apos;t find any results for the search &quot;{properties.data?.description.stem}&quot;
              </Text>
            </>
          )}
        </div>

        <SideOptions>
          {isSavable && (
            <SaveSearchWrapper
              onMouseEnter={(e) => {
                if (disableSaveSearch) {
                  openTooltip(
                    'You must click the "Search" button in the search form to update your results before you can save this search.',
                    e,
                  );
                }
              }}
              onMouseLeave={closeTooltip}
            >
              <Button
                containerStyle={{ height: 56 }}
                onClick={props.onSaveSearchClick}
                testID="saveThisSearch"
                icon={faBell}
                disabled={disableSaveSearch}
              >
                {properties.data?.savedSearch ? 'Edit Saved' : 'Save this'} Search
              </Button>
            </SaveSearchWrapper>
          )}

          <SelectInput
            ariaLabel="Select a sort order"
            containerStyle={{ minWidth: 200 }}
            id="sortOrderSelect"
            options={AUCTION_CATEGORIES.includes(filters.category) ? AUCTION_SORT_ORDER_OPTIONS : SORT_ORDER_OPTIONS}
            value={filters.sortOrder}
            onChange={props.handleSortChange}
            testID="searchSortOrder"
          />
        </SideOptions>
      </Container>

      <InfoContent>
        {properties.data &&
          properties.data.results.length > 0 &&
          properties.data?.alternativeSearches &&
          Object.keys(properties.data?.alternativeSearches).length > 0 && (
            <DidYouMean searchResult={properties.data} onClick={props.onAlternativeSearch} />
          )}

        {properties.data?.page === 0 && <OpenViewingBox searchResult={properties.data} wideDisplay />}

        {AUCTION_CATEGORIES.includes(filters.category) && <UpcomingAuctionsBox wideDisplay />}
      </InfoContent>
    </div>
  );
};

export default SearchTitle;
